import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';

// Weather AM / PM / Wind for the daily field report: three stacked rows, each
// with a monochrome glyph picked from the stored wording, and a small GPS
// refresh button. Used by the full report (inside the blue project card) and
// by the Quick Report review page (top identity card).

// Open-Meteo WMO weather codes → short field-report wording.
export const wxDesc = (code) => {
    if (code === 0) return 'Clear';
    if (code === 1) return 'Mostly clear';
    if (code === 2) return 'Partly cloudy';
    if (code === 3) return 'Overcast';
    if (code === 45 || code === 48) return 'Fog';
    if (code >= 51 && code <= 57) return 'Drizzle';
    if (code >= 61 && code <= 67) return 'Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Showers';
    if (code >= 95) return 'Thunderstorms';
    return 'Cloudy';
};

export const compass = (deg) => {
    if (deg == null || !Number.isFinite(deg)) return '';
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(deg / 45) % 8];
};

// Stored weather is free text ('Partly cloudy, 97°F'), so the glyph is picked
// off the wording rather than the original WMO code. Order matters — 'partly
// cloudy' has to be tested before plain 'cloudy', and 'mostly clear' before
// 'clear'.
export const wxKind = (value) => {
    const t = String(value || '').toLowerCase();
    if (!t) return 'none';
    if (t.includes('thunder') || t.includes('storm')) return 'storm';
    if (t.includes('snow') || t.includes('sleet')) return 'snow';
    if (t.includes('drizzle')) return 'drizzle';
    if (t.includes('rain') || t.includes('shower')) return 'rain';
    if (t.includes('fog') || t.includes('haze') || t.includes('mist')) return 'fog';
    if (t.includes('partly') || t.includes('mostly clear') || t.includes('mostly sunny')) return 'partly';
    if (t.includes('overcast') || t.includes('cloud')) return 'cloud';
    if (t.includes('clear') || t.includes('sunny') || t.includes('sun')) return 'sun';
    return 'cloud';
};

// Feather-style line glyphs, stroked in the current row colour. The compound
// icons scale the cloud with a transform, so their stroke width is scaled back
// up to keep every line the same visual weight.
const CLOUD = 'M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z';
const SUN_RAYS = ['M12 1v2', 'M12 21v2', 'M4.22 4.22l1.42 1.42', 'M18.36 18.36l1.42 1.42',
    'M1 12h2', 'M21 12h2', 'M4.22 19.78l1.42-1.42', 'M18.36 5.64l1.42-1.42'];

const glyph = (kind) => {
    switch (kind) {
        case 'sun':
            return (
                <>
                    <circle cx="12" cy="12" r="5" />
                    {SUN_RAYS.map((d) => <path key={d} d={d} />)}
                </>
            );
        case 'partly':
            return (
                <>
                    <circle cx="7" cy="7" r="3.2" />
                    <path d="M7 1.4v1.3" /><path d="M1.4 7h1.3" />
                    <path d="M3.05 3.05l.92.92" /><path d="M10.95 3.05l-.92.92" />
                    <path d="M3.05 10.95l.92-.92" />
                    <g transform="translate(9.6 9.2) scale(0.52)">
                        <path d={CLOUD} strokeWidth="3.1" />
                    </g>
                </>
            );
        case 'cloud':
            return <path d={CLOUD} />;
        case 'rain':
            return (
                <>
                    <g transform="translate(1.4 0.4) scale(0.88)">
                        <path d={CLOUD} strokeWidth="1.85" />
                    </g>
                    <path d="M8.5 20.5l-1 2.2" /><path d="M12.5 20.5l-1 2.2" /><path d="M16.5 20.5l-1 2.2" />
                </>
            );
        case 'drizzle':
            return (
                <>
                    <g transform="translate(1.4 0.4) scale(0.88)">
                        <path d={CLOUD} strokeWidth="1.85" />
                    </g>
                    <path d="M9.5 20.6v1.4" /><path d="M15 20.6v1.4" />
                </>
            );
        case 'snow':
            return (
                <>
                    <g transform="translate(1.4 0.4) scale(0.88)">
                        <path d={CLOUD} strokeWidth="1.85" />
                    </g>
                    <path d="M8 21.5h1.6" /><path d="M8.8 20.7v1.6" />
                    <path d="M14.4 21.5H16" /><path d="M15.2 20.7v1.6" />
                </>
            );
        case 'storm':
            return (
                <>
                    <g transform="translate(1.4 0.4) scale(0.88)">
                        <path d={CLOUD} strokeWidth="1.85" />
                    </g>
                    <path d="M13 19.5l-3 3.5h3.4l-1 2" />
                </>
            );
        case 'fog':
            return (
                <>
                    <g transform="translate(1.4 -2.2) scale(0.88)">
                        <path d={CLOUD} strokeWidth="1.85" />
                    </g>
                    <path d="M5 20h10" /><path d="M8 23h9" />
                </>
            );
        case 'wind':
            return (
                <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
            );
        default:
            // No reading yet — a plain dashed placeholder keeps the rows aligned.
            return <path d="M3 12h4M10 12h4M17 12h4" />;
    }
};

const WxIcon = ({ kind, color, size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flexShrink: 0, display: 'block' }}
    >
        {glyph(kind)}
    </svg>
);

const RefreshIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
        <path d="M23 4v6h-6M1 20v-6h6" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
);

// blue: sits inside the full report's blue project card (its own divider and
// label colour). bare: no divider — used when the block is a side column of a
// card rather than a band under it (the Quick Report review's identity card).
const WeatherBlock = ({ readOnly, blue, bare }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);

    // 'loading' | 'error' | null — GPS + Open-Meteo fill of the weather fields.
    const [wx, setWx] = useState(null);
    const fillWeather = () => {
        if (readOnly || wx === 'loading') return;
        if (!navigator.geolocation) { setWx('error'); return; }
        setWx('loading');
        navigator.geolocation.getCurrentPosition(async (pos) => {
            try {
                const { latitude, longitude } = pos.coords;
                const res = await fetch(
                    'https://api.open-meteo.com/v1/forecast'
                    + `?latitude=${latitude.toFixed(4)}&longitude=${longitude.toFixed(4)}`
                    + '&hourly=temperature_2m,weather_code'
                    + '&current=wind_speed_10m,wind_direction_10m'
                    + '&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto&forecast_days=1',
                );
                const data = await res.json();
                const at = (hh) => {
                    const i = (data.hourly?.time || []).findIndex((t) => t.endsWith(`T${hh}:00`));
                    if (i < 0) return null;
                    return `${wxDesc(data.hourly.weather_code[i])}, ${Math.round(data.hourly.temperature_2m[i])}°F`;
                };
                const wind = data.current && Number.isFinite(data.current.wind_speed_10m)
                    ? `${compass(data.current.wind_direction_10m)} ${Math.round(data.current.wind_speed_10m)} mph`.trim()
                    : null;
                setReport((r) => r ? {
                    ...r,
                    weatherAM: at('07') || r.weatherAM,
                    weatherPM: at('14') || r.weatherPM,
                    wind: wind || r.wind,
                } : r);
                setDirty(true);
                setWx(null);
            } catch (_) {
                setWx('error');
            }
        }, () => setWx('error'), { timeout: 10000, maximumAge: 300000 });
    };

    // Auto-fill today's weather once when a fresh (still-blank) report opens.
    // Reports that already carry weather skip the fetch so saved values are
    // never clobbered; the refresh button re-runs it on demand.
    const wxAutoRan = useRef(false);
    const hasWeather = !!(report && (report.weatherAM || report.weatherPM || report.wind));
    useEffect(() => {
        if (wxAutoRan.current || !report || readOnly || report.status === 'Submitted') return;
        wxAutoRan.current = true;
        if (!hasWeather) fillWeather();
    }, [report, readOnly, hasWeather]); // eslint-disable-line

    if (!report) return null;

    const labelColor = blue ? '#1D4ED8' : '#64748B';
    const iconColor = blue ? '#1D4ED8' : '#475569';
    const btnColor = wx === 'error' ? '#B91C1C' : '#1D4ED8';

    const rows = [
        ['Weather AM', report.weatherAM, wxKind(report.weatherAM)],
        ['Weather PM', report.weatherPM, wxKind(report.weatherPM)],
        ['Wind', report.wind, report.wind ? 'wind' : 'none'],
    ];

    return (
        <View style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            columnGap: 10,
            minWidth: bare ? 220 : 0,
            marginTop: bare ? 0 : blue ? 12 : 10,
            paddingTop: bare ? 0 : 10,
            borderTop: bare ? 'none' : blue ? '1px solid #BFDBFE' : '1px solid #E2E8F0',
        }}>
            <View style={{ flex: 1, rowGap: 3 }}>
                {rows.map(([k, v, kind]) => (
                    <View key={k} style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, minHeight: 19 }}>
                        <WxIcon kind={kind} color={v ? iconColor : '#CBD5E1'} />
                        <Text style={{
                            width: 72, fontSize: 10, fontWeight: '600', color: labelColor,
                            textTransform: 'uppercase', letterSpacing: 0.4,
                        }}>
                            {k}
                        </Text>
                        <Text style={{ fontSize: 12.5, color: v ? '#0F172A' : '#94A3B8' }}>{v || '—'}</Text>
                    </View>
                ))}
            </View>
            {!readOnly && (
                <Pressable
                    onPress={fillWeather}
                    title={wx === 'loading' ? 'Getting site weather…'
                        : wx === 'error' ? 'Weather unavailable — tap to retry'
                            : 'Refresh site weather (GPS)'}
                    aria-label="Refresh site weather"
                    style={{
                        width: 30, height: 30, borderRadius: 8,
                        alignItems: 'center', justifyContent: 'center',
                        backgroundColor: blue ? '#fff' : '#F8FAFC',
                        border: `1px solid ${wx === 'error' ? '#FECACA' : blue ? '#BFDBFE' : '#E2E8F0'}`,
                        opacity: wx === 'loading' ? 0.55 : 1,
                        cursor: wx === 'loading' ? 'default' : 'pointer',
                    }}
                >
                    <RefreshIcon color={btnColor} />
                </Pressable>
            )}
        </View>
    );
};

export default WeatherBlock;
